/**
 * Yetki alanlarının panel tarafındaki AYNASI.
 *
 * ⚠ Etkin alan listesini panel HESAPLAMAZ. Sunucu `/auth/me` yanıtında
 * `areas` alanını verir ve o hesabı `AreaPermissionGuard` ile aynı fonksiyon
 * yapar. Panelin `permissions ?? DEFAULT_AREAS_BY_ROLE[role]` kuralını burada
 * tekrarlaması iki kaynak demek olurdu ve rol varsayılanları değiştiğinde
 * menü ile gerçek yetki sessizce ayrışırdı.
 *
 * Buradaki liste yalnızca İKİ iş için var:
 *   1. Kullanıcılar sayfasındaki izin kutucuklarını çizmek (etiketleriyle),
 *   2. Rota → alan eşlemesi (RequireArea ve "ilk erişilebilir sayfa").
 */

export const PERMISSION_AREAS = [
  'dashboard',
  'invoices',
  'recurring',
  'contacts',
  'purchases',
  'expenses',
  'banking',
  'projects',
  'accounting',
  'vat-return',
  'einvoice',
  'assets',
  'reports',
  'compliance',
  'settings',
  'users',
  'audit-log',
  'export-import',
  'assistant',
] as const;

export type PermissionArea = (typeof PERMISSION_AREAS)[number];

export const AREA_LABELS: Record<PermissionArea, string> = {
  dashboard: 'Panel',
  invoices: 'Satış faturaları',
  recurring: 'Abonelikler',
  contacts: 'Müşteri & tedarikçi',
  purchases: 'Alış faturaları',
  expenses: 'Giderler',
  banking: 'Bankacılık',
  projects: 'İşler & projeler',
  accounting: 'Muhasebe defterleri',
  'vat-return': 'KDV beyanı',
  einvoice: 'E-fatura (Peppol)',
  assets: 'Duran varlıklar',
  reports: 'Raporlar',
  compliance: 'Uyum takvimi',
  settings: 'Ayarlar',
  users: 'Kullanıcılar',
  'audit-log': 'Denetim kaydı',
  'export-import': 'Dışa/içe aktarma',
  assistant: 'Yapay zekâ asistanı',
};

export const ROLES = ['ADMIN', 'ACCOUNTANT', 'VIEWER'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'Yönetici',
  ACCOUNTANT: 'Muhasebeci',
  VIEWER: 'Görüntüleyici',
};

/**
 * Salt-okuma rolü. Sunucuda `READ_ONLY_ROLES` ile aynı olmak ZORUNDA
 * (apps/api/src/common/constants/permission-areas.ts). Panel bunu yalnızca
 * "kaydet" düğmesini gizlemek için kullanır — asıl kapı sunucudadır.
 */
const READ_ONLY_ROLES = new Set<string>(['VIEWER']);

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  language: string;
  /** Ham DB değeri; null ise rol varsayılanı geçerlidir. Menü için KULLANMA. */
  permissions: string[] | null;
  twoFactorEnabled: boolean;
  /** Sunucunun hesapladığı ETKİN alan listesi. Menü ve rota kapısı bunu kullanır. */
  areas: string[];
}

export function isAdmin(user: Pick<SessionUser, 'role'> | null): boolean {
  return user?.role === 'ADMIN';
}

export function isReadOnly(user: Pick<SessionUser, 'role'> | null): boolean {
  return user !== null && READ_ONLY_ROLES.has(user.role);
}

export function hasArea(user: Pick<SessionUser, 'areas'> | null, area: PermissionArea): boolean {
  return user !== null && user.areas.includes(area);
}

/** Yalnızca ADMIN olmayan roller kendi izin listesini taşıyabilir. */
export function canHoldPermissions(role: Role): boolean {
  return role !== 'ADMIN';
}

/**
 * Rota → alan. RequireArea ve firstAllowedPath bunu okur.
 * SIRA ÖNEMLİ: giriş sonrası kullanıcı listedeki İLK erişebildiği sayfaya düşer.
 *
 * ⚠ `live` ALANI OLMADAN BU LİSTE SONSUZ DÖNGÜ ÜRETİR — ve bir zamanlar
 * üretiyordu. Liste henüz YAZILMAMIŞ milestone'ların rotalarını da taşıyor
 * (M3 alışlar, M4 banka, M5 KDV beyanı…) çünkü plan burada da okunuyor. Ama
 * yalnızca o alanlardan birine yetki verilmiş bir kullanıcı için sıra şuydu:
 *
 *   `/` → HomeGate (dashboard yok) → firstAllowedPath → `/banking`
 *   `/banking` → hiçbir rotayla eşleşmez → `<Route path="*">` → `/`
 *   `/` → … ve baştan.
 *
 * Tarayıcı kilitlenir; React "Maximum update depth exceeded" atar. Bu yüzden
 * `firstAllowedPath` YALNIZCA `live: true` olanları döndürür ve gerisi için
 * "yetkiniz var ama sayfa henüz yok" ekranı gösterilir — dönmeyen bir sayfa,
 * dönen bir döngüden iyidir.
 *
 * ⚠ Yeni bir sayfa `App.tsx`e bağlandığında `live`i BURADA da açın.
 */
export const ROUTE_AREAS: ReadonlyArray<{
  path: string;
  area: PermissionArea;
  /** `App.tsx`te GERÇEKTEN bağlı mı. */
  live: boolean;
}> = [
  { path: '/', area: 'dashboard', live: true },
  { path: '/invoices', area: 'invoices', live: true },
  { path: '/contacts', area: 'contacts', live: true },
  { path: '/purchases', area: 'purchases', live: true },
  { path: '/expenses', area: 'expenses', live: true },
  { path: '/accounting', area: 'accounting', live: true },
  { path: '/subscriptions', area: 'recurring', live: true },
  { path: '/projects', area: 'projects', live: true },
  { path: '/banking', area: 'banking', live: true },
  { path: '/vat-return', area: 'vat-return', live: true },
  { path: '/einvoice', area: 'einvoice', live: true },
  { path: '/assets', area: 'assets', live: true },
  /*
   * ⚠ HENÜZ BAĞLI DEĞİL (M4 — geçmiş veri içe aktarma; bugün yalnız açılış
   * fişi ve geçmiş fatura aktarımı var, ayrı bir ekranı yok). Listede DURMASI
   * gerekiyor: `firstAllowedPath` bağlanmamış yolları atlamayı bırakırsa
   * sonsuz yönlendirme döngüsü doğuyor ve o kuralı canlı tutan tek şey,
   * listede gerçekten `live: false` bir satırın bulunmasıdır.
   */
  { path: '/export-import', area: 'export-import', live: false },
  { path: '/compliance', area: 'compliance', live: true },
  { path: '/reports', area: 'reports', live: true },
  { path: '/settings', area: 'settings', live: true },
  { path: '/users', area: 'users', live: true },
  { path: '/audit-log', area: 'audit-log', live: true },
  { path: '/assistant', area: 'assistant', live: true },
];

/**
 * Kullanıcının girebileceği ilk sayfa.
 *
 * Kök rotayı sabit `/` yapmak, `dashboard` yetkisi olmayan bir kullanıcıyı
 * giriş sonrası boş ekrana düşürürdü. Hiçbir alana yetkisi yoksa `null` döner
 * ve arayüz "yetkiniz yok" ekranını gösterir — sonsuz yönlendirme döngüsü
 * yerine açık bir mesaj.
 */
export function firstAllowedPath(user: Pick<SessionUser, 'areas'> | null): string | null {
  if (!user) return null;
  for (const route of ROUTE_AREAS) {
    // ⚠ `live` denetimi ŞART: bağlanmamış bir yola yönlendirmek, `*` rotasının
    // kullanıcıyı köke geri atmasıyla birleşince SONSUZ DÖNGÜ üretir.
    if (route.live && user.areas.includes(route.area)) return route.path;
  }
  return null;
}
